import { Fingerprint, Mail } from "lucide-react";
import type { JSX } from "react";
import { type FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthStatus } from "@/common/enums/enums";
import {
  NativeTotpEnrollment,
  totpEnrollmentSchema,
} from "@/components/ui/NativeTotpEnrollment";
import {
  AppleMark,
  AUTH_INPUT,
  AUTH_LABEL,
  AUTH_LINK,
  AUTH_PRIMARY,
  AUTH_SECONDARY,
  AuthDivider,
  AuthNotice,
  AuthShell,
  GoogleMark,
} from "@/components/ui/native-auth/AuthShell";
import { CodeInput } from "@/components/ui/native-auth/CodeInput";
import { pendingLaunchUrl } from "@/lib/emr-launch";
import {
  accountUnavailableMessage,
  authProviderUnavailableMessage,
  nativeAuthRequest,
  needsRecentSignIn,
} from "@/lib/native-auth";
import { useNativeAuthStore } from "@/store/native-auth.store";
import { NativeRegistration } from "./NativeRegistration";

// Better Auth sign-in and sign-up. Google, Apple or an
// emailed code signs in, or creates the account for a new email; passkeys
// sign in.

export const RESEND_SECONDS = 30;
const CODE_LENGTH = 6;
// Holds the handoff state once this sign-in window showed the sign-in form.
const HANDOFF_SIGN_IN_KEY = "rosc.native-handoff-sign-in";
type Notice = { tone: "info" | "error" | "success"; text: string };

export const NativeLogin = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithEmailOtp,
    signInWithPasskey,
    signInWithPasskeyAutofill,
    session,
    challengeRequired,
    registrationRequired,
    authStatus,
    refresh,
    loading,
    error: storeError,
  } = useNativeAuthStore();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // A refused Google or Apple sign-in returns here with ?error=<code>.
  const [notice, setNotice] = useState<Notice | null>(() => {
    const failure = new URLSearchParams(window.location.search).get("error");
    if (!failure) return null;
    return {
      tone: "error",
      text:
        failure === "ACCOUNT_UNAVAILABLE"
          ? accountUnavailableMessage()
          : failure === "AUTH_PROVIDER_UNAVAILABLE"
            ? authProviderUnavailableMessage()
            : t("auth.native.notices.socialFailed"),
    };
  });
  const [busy, setBusy] = useState(false);
  const [enrollment, setEnrollment] = useState<z.infer<
    typeof totpEnrollmentSchema
  > | null>(null);
  const [codesSaved, setCodesSaved] = useState(false);
  const [confirmAccount, setConfirmAccount] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const handoff = params.has("clientId");
  const handoffState = params.get("state") ?? "";
  const working = busy || loading;

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    // Google sign-in reloads this page, so remember across the redirect.
    if (handoff && authStatus === AuthStatus.UNAUTHENTICATED && !loading)
      sessionStorage.setItem(HANDOFF_SIGN_IN_KEY, handoffState);
  }, [authStatus, handoff, handoffState, loading]);

  const emailFormShown =
    step === "email" && !session && !challengeRequired && !registrationRequired;
  useEffect(() => {
    if (emailFormShown) void signInWithPasskeyAutofill();
  }, [emailFormShown, signInWithPasskeyAutofill]);

  const authorize = (isActive: () => boolean = () => true) =>
    nativeAuthRequest(
      "client/authorize",
      Object.fromEntries(
        ["clientId", "redirectUri", "state", "challenge"].map((key) => [
          key,
          params.get(key),
        ]),
      ),
    )
      .then((value) => {
        const result = z.object({ redirectUrl: z.url() }).parse(value);
        const expected = new URL(params.get("redirectUri") ?? "");
        const target = new URL(result.redirectUrl);
        if (
          target.protocol !== expected.protocol ||
          target.host !== expected.host ||
          target.pathname !== expected.pathname ||
          target.username ||
          target.password
        )
          throw Error("Unexpected client callback");
        sessionStorage.removeItem(HANDOFF_SIGN_IN_KEY);
        if (isActive()) window.location.assign(result.redirectUrl);
      })
      .catch(async (error: unknown) => {
        if (!isActive()) return;
        setConfirmAccount(false);
        // An older session cannot authorize an app; sign in again here.
        if (needsRecentSignIn(error)) {
          await useNativeAuthStore.getState().signOut();
          setNotice({
            tone: "info",
            text: t("auth.native.notices.signInAgain"),
          });
          return;
        }
        setNotice({
          tone: "error",
          text: t("auth.native.notices.authorizeFailed"),
        });
      });

  // biome-ignore lint/correctness/useExhaustiveDependencies: authorize reads only the URL and stable store actions.
  useEffect(() => {
    if (authStatus !== AuthStatus.AUTHENTICATED) return;
    if (!handoff) {
      // Continue an EMR launch that needed sign-in first.
      window.location.assign(pendingLaunchUrl() ?? "/visits");
      return;
    }
    // An existing browser session must not pick the app's account silently.
    if (sessionStorage.getItem(HANDOFF_SIGN_IN_KEY) !== handoffState) {
      setConfirmAccount(true);
      return;
    }
    let active = true;
    void authorize(() => active);
    return () => {
      active = false;
    };
  }, [authStatus, handoff, handoffState]);

  const run = async (operation: () => Promise<unknown>) => {
    setBusy(true);
    setNotice(null);
    useNativeAuthStore.setState({ error: null });
    try {
      await operation();
    } catch (error) {
      setNotice({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : t("auth.native.notices.requestFailed"),
      });
    } finally {
      setBusy(false);
    }
  };
  const sendCode = async () => {
    await nativeAuthRequest("email-otp/send-verification-otp", {
      email: email.trim(),
      type: "sign-in",
    });
    setOtp("");
    setStep("code");
    setResendIn(RESEND_SECONDS);
  };
  const resendCode = () =>
    void run(async () => {
      await sendCode();
      setNotice({ tone: "success", text: t("auth.native.notices.codeResent") });
    });
  const changeEmail = () => {
    setStep("email");
    setOtp("");
    setNotice(null);
  };
  const signOut = () =>
    void run(async () => {
      await useNativeAuthStore.getState().signOut();
      setStep("email");
      setOtp("");
      setCode("");
      setEnrollment(null);
      setConfirmAccount(false);
    });

  const submitEmail = (event: FormEvent) => {
    event.preventDefault();
    void run(sendCode);
  };
  const submitOtp = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await signInWithEmailOtp(email.trim(), otp);
      // The store keeps a failed sign-in as `error` instead of throwing.
      const { error } = useNativeAuthStore.getState();
      if (!error) return;
      setOtp("");
      useNativeAuthStore.setState({ error: null });
      setNotice({
        tone: "error",
        text:
          error === accountUnavailableMessage() ||
          error === authProviderUnavailableMessage()
            ? error
            : t("auth.native.notices.codeRejected"),
      });
    });
  };
  const submitSecondFactor = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (enrollment && !codesSaved)
        throw Error(t("auth.native.notices.saveRecoveryCodesFirst"));
      await nativeAuthRequest(
        backup ? "two-factor/verify-backup-code" : "two-factor/verify-totp",
        { code: code.trim() },
      );
      setCode("");
      setEnrollment(null);
      useNativeAuthStore.setState({ challengeRequired: false });
      await refresh();
    });
  };

  // A store error outside an action, such as a session that failed to
  // load after the Google redirect, still needs to be visible.
  const shown: Notice | null =
    notice ?? (storeError ? { tone: "error", text: storeError } : null);
  const noticeView = shown ? (
    <AuthNotice className="mb-5" tone={shown.tone}>
      {shown.text}
    </AuthNotice>
  ) : null;
  const switchAccount = (
    <button
      className={AUTH_LINK}
      disabled={working}
      type="button"
      onClick={signOut}
    >
      {t("auth.native.useDifferentAccount")}
    </button>
  );

  if (registrationRequired)
    return <NativeRegistration footer={switchAccount} handoff={handoff} />;

  if (session && !session.user.emailVerified)
    return (
      <AuthShell
        description={t("auth.native.verifyEmail.description", {
          email: session.user.email,
        })}
        footer={switchAccount}
        title={t("auth.native.verifyEmail.title")}
      >
        {noticeView}
        <div className="flex flex-col gap-3">
          <button
            className={AUTH_PRIMARY}
            disabled={working}
            type="button"
            onClick={() =>
              void run(async () => {
                await nativeAuthRequest("send-verification-email", {
                  email: session.user.email,
                  callbackURL: window.location.href,
                });
                setNotice({
                  tone: "success",
                  text: t("auth.native.verifyEmail.sent"),
                });
              })
            }
          >
            {t("auth.native.verifyEmail.send")}
          </button>
          <button
            className={AUTH_SECONDARY}
            disabled={working}
            type="button"
            onClick={() => void run(refresh)}
          >
            {t("auth.native.verifyEmail.done")}
          </button>
        </div>
      </AuthShell>
    );

  if (session?.enrollmentRequired)
    return (
      <AuthShell
        description={t("auth.native.enroll.description")}
        footer={switchAccount}
        title={t("auth.native.enroll.title")}
      >
        {noticeView}
        {enrollment ? (
          <form className="flex flex-col gap-5" onSubmit={submitSecondFactor}>
            <NativeTotpEnrollment
              enrollment={enrollment}
              saved={codesSaved}
              onSaved={setCodesSaved}
            />
            <div>
              <label className={AUTH_LABEL} htmlFor="native-code">
                {t("auth.native.authenticatorCode")}
              </label>
              <CodeInput
                disabled={working}
                id="native-code"
                value={code}
                onChange={setCode}
              />
            </div>
            <button
              className={AUTH_PRIMARY}
              disabled={working || code.length < CODE_LENGTH}
              type="submit"
            >
              {t("auth.native.verify")}
            </button>
          </form>
        ) : (
          <button
            className={AUTH_PRIMARY}
            disabled={working}
            type="button"
            onClick={() =>
              void run(async () => {
                setEnrollment(
                  totpEnrollmentSchema.parse(
                    await nativeAuthRequest("two-factor/enable", {}),
                  ),
                );
              })
            }
          >
            {t("auth.native.enroll.start")}
          </button>
        )}
      </AuthShell>
    );

  if (session?.mfaRequired || challengeRequired)
    return (
      <AuthShell
        description={
          backup
            ? t("auth.native.challenge.recoveryDescription")
            : t("auth.native.challenge.description")
        }
        footer={switchAccount}
        title={t("auth.native.challenge.title")}
      >
        {noticeView}
        <form className="flex flex-col gap-5" onSubmit={submitSecondFactor}>
          {backup ? (
            <div>
              <label className={AUTH_LABEL} htmlFor="native-code">
                {t("auth.native.challenge.recoveryCode")}
              </label>
              <input
                autoCapitalize="none"
                autoComplete="one-time-code"
                className={`${AUTH_INPUT} font-mono`}
                disabled={working}
                id="native-code"
                required
                spellCheck={false}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className={AUTH_LABEL} htmlFor="native-code">
                {t("auth.native.authenticatorCode")}
              </label>
              <CodeInput
                autoFocus
                disabled={working}
                id="native-code"
                value={code}
                onChange={setCode}
              />
            </div>
          )}
          <button
            className={AUTH_PRIMARY}
            disabled={
              working || (backup ? !code.trim() : code.length < CODE_LENGTH)
            }
            type="submit"
          >
            {t("auth.native.verify")}
          </button>
          <label className="flex cursor-pointer items-center justify-center gap-2 text-[14px] text-v3-text-2">
            <input
              checked={backup}
              className="size-4 accent-v3-brand"
              type="checkbox"
              onChange={(event) => {
                setBackup(event.target.checked);
                setCode("");
              }}
            />
            {t("auth.native.challenge.useRecoveryCode")}
          </label>
        </form>
      </AuthShell>
    );

  if (confirmAccount && session && authStatus === AuthStatus.AUTHENTICATED)
    return (
      <AuthShell
        description={
          <Trans
            components={{
              email: <span className="font-semibold text-v3-text" />,
            }}
            i18nKey="auth.native.confirmAccount.description"
            values={{ email: session.user.email }}
          />
        }
        footer={switchAccount}
        title={t("auth.native.confirmAccount.title")}
      >
        {noticeView}
        <button
          className={AUTH_PRIMARY}
          disabled={working}
          type="button"
          onClick={() => void run(() => authorize())}
        >
          {t("auth.native.confirmAccount.continueAs", {
            email: session.user.email,
          })}
        </button>
      </AuthShell>
    );

  if (step === "code")
    return (
      <AuthShell
        description={
          <Trans
            components={{
              email: <span className="font-semibold text-v3-text" />,
            }}
            i18nKey="auth.native.code.description"
            values={{ email: email.trim() }}
          />
        }
        footer={
          <p className="max-w-[320px] text-[13px] text-v3-text-3">
            {t("auth.native.code.noEmail")}
          </p>
        }
        title={t("auth.native.code.title")}
      >
        {noticeView}
        <form className="flex flex-col gap-5" onSubmit={submitOtp}>
          <div>
            <label className="sr-only" htmlFor="native-email-otp">
              {t("auth.native.code.label")}
            </label>
            <CodeInput
              autoFocus
              disabled={working}
              id="native-email-otp"
              invalid={notice?.tone === "error"}
              value={otp}
              onChange={(value) => {
                setOtp(value);
                if (notice?.tone === "error") setNotice(null);
              }}
            />
          </div>
          <button
            className={AUTH_PRIMARY}
            disabled={working || otp.length < CODE_LENGTH}
            type="submit"
          >
            {t("login.form.signInButton")}
          </button>
        </form>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[14px]">
          <button
            className={AUTH_LINK}
            disabled={working || resendIn > 0}
            type="button"
            onClick={resendCode}
          >
            {resendIn > 0
              ? t("auth.native.code.resendIn", { count: resendIn })
              : t("auth.native.code.resend")}
          </button>
          <button
            className={AUTH_LINK}
            disabled={working}
            type="button"
            onClick={changeEmail}
          >
            {t("auth.native.code.changeEmail")}
          </button>
        </div>
      </AuthShell>
    );

  return (
    <AuthShell
      description={t("auth.native.signIn.description")}
      footer={
        <button
          className={`${AUTH_LINK} inline-flex items-center gap-1.5 font-medium text-v3-text-2 hover:text-v3-text dark:text-v3-text-2`}
          disabled={working}
          type="button"
          onClick={() => void run(signInWithPasskey)}
        >
          <Fingerprint aria-hidden className="size-4" />
          {t("auth.native.signIn.passkey")}
        </button>
      }
      title={t("auth.native.signIn.title")}
    >
      {noticeView}
      <div className="flex flex-col gap-3">
        <button
          className={AUTH_SECONDARY}
          disabled={working}
          type="button"
          onClick={() => void run(signInWithGoogle)}
        >
          <GoogleMark />
          {t("auth.googleSignInButton")}
        </button>
        <button
          className={AUTH_SECONDARY}
          disabled={working}
          type="button"
          onClick={() => void run(signInWithApple)}
        >
          <AppleMark />
          {t("auth.native.signIn.apple")}
        </button>
      </div>
      <AuthDivider label={t("auth.native.signIn.or")} />
      <form className="flex flex-col gap-4" onSubmit={submitEmail}>
        <div>
          <label className={AUTH_LABEL} htmlFor="native-email">
            {t("login.form.emailLabel")}
          </label>
          <input
            autoCapitalize="none"
            autoComplete="username webauthn"
            className={AUTH_INPUT}
            disabled={working}
            id="native-email"
            inputMode="email"
            placeholder="you@clinic.com"
            required
            spellCheck={false}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <button
          className={AUTH_PRIMARY}
          disabled={working || !email.trim()}
          type="submit"
        >
          <Mail aria-hidden className="size-4" />
          {t("auth.native.signIn.sendCode")}
        </button>
      </form>
    </AuthShell>
  );
};
